import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, Info, Percent, RefreshCw, TrendingUp, Users } from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/finance`;

const fxof = (v) => `${Math.round(Number(v || 0)).toLocaleString('fr-FR')} F`;
const monthLabel = (m) => {
  if (!m) return '—';
  const [y, mm] = m.split('-');
  return `${['', 'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'][Number(mm)]} ${y.slice(2)}`;
};

const Kpi = ({ label, value, sub, icon: Icon, accent = 'violet', testId }) => (
  <div className={`rounded-xl border border-${accent}-200 bg-${accent}-50/70 p-3`} data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className="mt-1 text-xl font-bold text-gray-900">{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-gray-500">{sub}</div>}
  </div>
);

/** Cockpit CFO — ce que la majoration cachée de 2 % rapporte réellement, par mois et par client. */
export const HiddenTaxTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState('6');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/cockpit/hidden-tax`, { params: { months } });
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rapport taxe 2 % indisponible');
    } finally { setLoading(false); }
  }, [months]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Skeleton className="h-96 w-full rounded-xl" />;
  if (!data) return null;

  const t = data.totals || {};
  const cur = data.currentMonth;
  const chart = (data.monthly || []).map((m) => ({ ...m, label: monthLabel(m.month) }));
  const maxTax = Math.max(...chart.map((c) => c.tax), 1);

  return (
    <div className="space-y-4" data-testid="hidden-tax-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Coins className="h-4 w-4 text-violet-600" /> Taxe globale 2 %
                <Badge variant="outline"
                  className={data.enabled ? 'border-emerald-300 bg-emerald-50 text-[10px] text-emerald-700' : 'border-gray-300 text-[10px] text-gray-500'}>
                  {data.enabled ? 'Activée par défaut' : 'Désactivée par défaut'}
                </Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                Majoration incorporée aux prix unitaires, invisible pour le client. Ce rapport ne
                compte que les devis et factures où elle a réellement été appliquée.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={months} onValueChange={setMonths}>
                <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="hidden-tax-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 derniers mois</SelectItem>
                  <SelectItem value="6">6 derniers mois</SelectItem>
                  <SelectItem value="12">12 derniers mois</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={load}
                data-testid="hidden-tax-refresh">
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Actualiser
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kpi testId="hidden-tax-kpi-total" label={`Taxe sur ${months} mois`} value={fxof(t.tax)}
              sub={`${t.operations || 0} opération(s) concernée(s)`} icon={Coins} />
            <Kpi testId="hidden-tax-kpi-month" label="Mois en cours"
              value={fxof(cur?.tax)} icon={TrendingUp} accent="emerald"
              sub={data.momPct !== null && data.momPct !== undefined
                ? `${data.momPct > 0 ? '+' : ''}${data.momPct} % vs mois précédent`
                : 'Pas de comparaison disponible'} />
            <Kpi testId="hidden-tax-kpi-settled" label="Déjà encaissée" value={fxof(t.taxSettled)}
              sub="Factures soldées" icon={Coins} accent="blue" />
            <Kpi testId="hidden-tax-kpi-rate" label="Taux effectif"
              value={`${t.effectiveRatePct || 0} %`} sub={`Base HT ${fxof(t.baseHT)}`} icon={Percent} accent="amber" />
            <Kpi testId="hidden-tax-kpi-clients" label="Clients concernés" value={t.clients || 0}
              sub="Sur la période" icon={Users} accent="slate" />
          </div>

          {t.tax > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8"
                    tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip
                    formatter={(v, n) => [fxof(v), n === 'tax' ? 'Taxe 2 %' : 'Encaissée']}
                    labelFormatter={(l) => `Mois : ${l}`}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Bar dataKey="tax" radius={[6, 6, 0, 0]} name="tax">
                    {chart.map((c) => (
                      <Cell key={c.month} fill={c.tax >= maxTax ? '#7c3aed' : '#c4b5fd'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-3 text-xs text-blue-800"
              data-testid="hidden-tax-empty">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Aucune taxe 2 % appliquée sur la période. Elle s&apos;applique aux nouveaux devis
                quand elle est activée dans Paramètres → FNE, ou manuellement devis par devis.
              </span>
            </div>
          )}

          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Par client</h3>
            {(data.clients || []).length === 0 ? (
              <p className="rounded-lg bg-gray-50 px-3 py-3 text-xs text-gray-400">Aucun client sur la période.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs" data-testid="hidden-tax-client-table">
                  <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Client</th>
                      <th className="px-3 py-2 text-right font-medium">Ops</th>
                      <th className="px-3 py-2 text-right font-medium">Base HT réelle</th>
                      <th className="px-3 py-2 text-right font-medium">Taxe 2 %</th>
                      <th className="px-3 py-2 text-right font-medium">Dont encaissée</th>
                      <th className="px-3 py-2 text-right font-medium">Part du CA</th>
                      <th className="px-3 py-2 text-left font-medium">Dernier mois</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.clients.map((c) => (
                      <tr key={c.client} className="hover:bg-violet-50/40">
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {c.client}
                          {c.sources?.includes('contrat') && (
                            <Badge variant="outline" className="ml-1.5 border-blue-200 text-[9px] text-blue-600">contrat</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">{c.operations}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{fxof(c.baseHT)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-violet-700">{fxof(c.tax)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{fxof(c.taxSettled)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{c.sharePct} %</td>
                        <td className="px-3 py-2 text-gray-500">{monthLabel(c.lastMonth)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HiddenTaxTab;
